import { useSyncExternalStore } from 'react';
import { getLocale, subscribeLocale, type Locale } from './locale';
import esCommon from './messages/es/common';
import esOnboarding from './messages/es/onboarding';
import esWorkspace from './messages/es/workspace';
import esScreening from './messages/es/screening';
import esAgency from './messages/es/agency';
import esAdmin from './messages/es/admin';
import esPlan from './messages/es/plan';
import esPartner from './messages/es/partner';
import esEnglish from './messages/es/english';
import esCredits from './messages/es/credits';
import enCommon from './messages/en/common';
import enOnboarding from './messages/en/onboarding';
import enWorkspace from './messages/en/workspace';
import enScreening from './messages/en/screening';
import enAgency from './messages/en/agency';
import enAdmin from './messages/en/admin';
import enPlan from './messages/en/plan';
import enPartner from './messages/en/partner';
import enEnglish from './messages/en/english';
import enCredits from './messages/en/credits';

/**
 * Los textos de la app, por idioma y por área. Un área por audiencia/pantalla
 * grande para que cada archivo se lea entero y dos personas no se pisen:
 *
 *   common      shell, login, componentes compartidos, errores genéricos
 *   onboarding  alta, conexión, hoja de vida, preferencias, bolsa de talento
 *   workspace   panel, mensajes, hilo, postulaciones, invitaciones
 *   screening   el cuestionario (preguntas, voz, coach, revisión)
 *   agency      la consola de agencia
 *   admin       la consola de operación
 *   plan        el plan PRO: oferta, paywall, cupón, página del plan
 *   partner     el Programa de socios (/socios)
 *   english     la evaluación de inglés por llamada (agencia, candidato, operación)
 *   credits     las evaluaciones de inglés prepagadas de las agencias (saldo, compra)
 *
 * El español es la fuente y define el tipo; el inglés tiene que calzar clave
 * por clave. No hay "fallback" en tiempo de ejecución: una clave faltante es un
 * error de compilación, no un texto en blanco en producción.
 */
const catalogs = {
  es: {
    common: esCommon,
    onboarding: esOnboarding,
    workspace: esWorkspace,
    screening: esScreening,
    agency: esAgency,
    admin: esAdmin,
    plan: esPlan,
    partner: esPartner,
    english: esEnglish,
    credits: esCredits,
  },
  en: {
    common: enCommon,
    onboarding: enOnboarding,
    workspace: enWorkspace,
    screening: enScreening,
    agency: enAgency,
    admin: enAdmin,
    plan: enPlan,
    partner: enPartner,
    english: enEnglish,
    credits: enCredits,
  },
} satisfies Record<Locale, unknown>;

export type Messages = (typeof catalogs)['es'];
export type Namespace = keyof Messages;

/** Los textos de un idioma, fuera de React (reglas puras de `lib/`). */
export function messages(locale: Locale = getLocale()): Messages {
  return catalogs[locale];
}

/** El idioma activo; re-renderiza al cambiarlo. */
export function useLocale(): Locale {
  return useSyncExternalStore(subscribeLocale, getLocale, getLocale);
}

/**
 * Los textos de un área en el idioma activo:
 *
 *   const t = useT('workspace');
 *   <h1>{t.dashboard.title}</h1>
 *   <span>{t.inbox.unread(3)}</span>
 */
export function useT<N extends Namespace>(namespace: N): Messages[N] {
  return catalogs[useLocale()][namespace];
}

export {
  getLocale,
  setLocale,
  subscribeLocale,
  detectLocale,
  intlLocale,
  LOCALES,
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  type Locale,
} from './locale';
export { formatDate, formatDateTime, formatTime, formatNumber } from './format';
